import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { trackEvent } from '@/utils/analytics';

import { getInviteUrl, teamApis } from '../team.query';
import { teamStore } from '../team.store';
import {
  MemberTierEnum,
  TeamRoleEnum,
  type MemberTier,
  type TeamMember,
  type TeamRole,
} from '../team.types';

interface InviteMemberModalProps {
  teamId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const InviteMemberModal = observer(function InviteMemberModal({
  teamId,
  open,
  onOpenChange,
}: InviteMemberModalProps) {
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState<MemberTier>(MemberTierEnum.enum.plus);
  const [role, setRole] = useState<Exclude<TeamRole, 'owner'>>(
    TeamRoleEnum.enum.member
  );
  const [invitedMember, setInvitedMember] = useState<TeamMember | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setError(null);
    try {
      const member = await teamApis.inviteMember(teamId, {
        email: email.trim(),
        tier,
        role,
      });
      setInvitedMember(member);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invite');
    }
  };

  const handleCopy = async () => {
    if (!invitedMember?.invite_token) return;
    await navigator.clipboard.writeText(
      getInviteUrl(invitedMember.invite_token)
    );
    trackEvent('TEAM_INVITE_LINK_COPIED', { source: 'post_invite' });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    onOpenChange(false);
    setEmail('');
    setTier(MemberTierEnum.enum.plus);
    setRole(TeamRoleEnum.enum.member);
    setError(null);
    setInvitedMember(null);
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && handleClose()}>
      <Dialog.Content className="max-w-md" showCloseButton={false}>
        {invitedMember ? (
          <YStack className="gap-5">
            <YStack className="gap-1 items-center text-center">
              <div className="rounded-full bg-success-3 p-2.5 mb-1">
                <Icons.check className="w-5 h-5 text-success-11" />
              </div>
              <h3 className="text-base font-semibold text-foreground">
                Invite sent
              </h3>
              <p className="text-sm text-foreground-muted">
                Share this link with{' '}
                <span className="font-medium text-foreground">
                  {invitedMember.email}
                </span>{' '}
                to join your team.
              </p>
            </YStack>

            <XStack className="items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
              <span className="flex-1 text-xs text-foreground-muted select-all break-all line-clamp-1">
                {getInviteUrl(invitedMember.invite_token!)}
              </span>
              <Button
                variant="ghost"
                size="xs"
                onClick={handleCopy}
                className="shrink-0"
              >
                {copied ? (
                  <Icons.check className="w-3.5 h-3.5 text-success-11" />
                ) : (
                  <Icons.copy className="w-3.5 h-3.5" />
                )}
              </Button>
            </XStack>

            <XStack className="justify-end">
              <Button variant="default" size="sm" onClick={handleClose}>
                Done
              </Button>
            </XStack>
          </YStack>
        ) : (
          <form onSubmit={handleSubmit}>
            <YStack className="gap-5">
              <YStack className="gap-1">
                <h3 className="text-base font-semibold text-foreground">
                  Invite team member
                </h3>
                <p className="text-sm text-foreground-muted">
                  They'll receive a link to join your team.
                </p>
              </YStack>

              <YStack className="gap-4">
                <Input
                  id="invite-email"
                  type="email"
                  label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  required
                  autoFocus
                />

                <YStack className="gap-2">
                  <label className="text-foreground text-sm font-sans">
                    Role
                  </label>
                  <SegmentedControl.Root
                    value={role}
                    onValueChange={(value) =>
                      setRole(value as Exclude<TeamRole, 'owner'>)
                    }
                  >
                    <SegmentedControl.List className="w-full">
                      <SegmentedControl.Trigger
                        value="member"
                        className="flex-1"
                      >
                        Member
                      </SegmentedControl.Trigger>
                      <SegmentedControl.Trigger
                        value="admin"
                        className="flex-1"
                      >
                        Admin
                      </SegmentedControl.Trigger>
                    </SegmentedControl.List>
                  </SegmentedControl.Root>
                </YStack>

                <YStack className="gap-2">
                  <label className="text-foreground text-sm font-sans">
                    Plan
                  </label>
                  <SegmentedControl.Root
                    value={tier}
                    onValueChange={(value) => setTier(value as MemberTier)}
                  >
                    <SegmentedControl.List className="w-full">
                      <SegmentedControl.Trigger value="plus" className="flex-1">
                        Plus
                      </SegmentedControl.Trigger>
                      <SegmentedControl.Trigger
                        value="power"
                        className="flex-1"
                      >
                        Power
                      </SegmentedControl.Trigger>
                      <SegmentedControl.Trigger value="pro" className="flex-1">
                        Pro
                      </SegmentedControl.Trigger>
                      <SegmentedControl.Trigger value="pro2" className="flex-1">
                        Pro2
                      </SegmentedControl.Trigger>
                    </SegmentedControl.List>
                  </SegmentedControl.Root>
                  <SeatCostPreview tier={tier} />
                </YStack>

                {error && (
                  <XStack className="items-center gap-2 rounded-lg border border-danger-5 dark:border-danger-3 bg-danger-2 px-3 py-2">
                    <Icons.warnTriangle className="w-3.5 h-3.5 text-danger-11 shrink-0" />
                    <span className="text-sm text-danger-11">{error}</span>
                  </XStack>
                )}

                <XStack className="gap-2 justify-end pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClose}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    type="submit"
                    isLoading={teamStore.inviteLoading}
                    disabled={!email.trim()}
                  >
                    Send Invite
                  </Button>
                </XStack>
              </YStack>
            </YStack>
          </form>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
});

const SeatCostPreview = observer(function SeatCostPreview({
  tier,
}: {
  tier: MemberTier;
}) {
  const interval = teamStore.dashboard?.billing.billing_interval;
  if (!interval) return null;

  const cents = teamStore.getPriceCents(tier, interval);
  if (cents === null) return null;

  const intervalLabel = interval === 'annual' ? 'mo (billed annually)' : 'mo';
  const amount = `$${(cents / 100).toFixed(2)}`;

  return (
    <YStack className="gap-0.5">
      <span className="text-sm text-foreground-muted">
        Adds a seat at{' '}
        <span className="text-foreground font-medium">
          {amount}/{intervalLabel}
        </span>
        .
      </span>
      <span className="text-sm text-foreground-muted">
        Your next bill will be prorated.
      </span>
    </YStack>
  );
});
